/// FOURJS_START_COPYRIGHT(D,2026)
/// Property of Four Js*
/// (c) Copyright Four Js 2026, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("TableConfigSidePanelWidget", ["SidePanelWidget", "FilterableListWidget", "WidgetFactory"], function (context, cls) {
  /**
   * Side panel to configure a table (column visibility, order, sizing).
   * Bound to the table widget being configured; changes are applied immediately
   * through the regular column events and reflected back on VM acknowledgement.
   * @class TableConfigSidePanelWidget
   * @memberOf classes
   * @extends classes.SidePanelWidget
   */
  cls.TableConfigSidePanelWidget = context.oo.Class(cls.SidePanelWidget, function ($super) {
    return /** @lends classes.TableConfigSidePanelWidget.prototype */ {
      __name: "TableConfigSidePanelWidget",
      __templateName: "SidePanelWidget",

      /**
       * The table widget being configured
       * @type {classes.TableWidgetBase|null}
       */
      _tableWidget: null,

      /**
       * The columns list
       * @type {classes.FilterableListWidget|null}
       */
      _listWidget: null,

      /**
       * List item id → column widget
       * @type {?Map<string, classes.RTableColumnWidget>}
       */
      _columnsById: null,

      /**
       * Unbinds the table columns-changed listener
       * @type {?HandleRegistration}
       */
      _tableColumnsChangedHandle: null,

      /**
       * True when a list refresh is already queued
       * @type {boolean}
       */
      _refreshQueued: false,

      /**
       * The "Autofit" columns size button
       * @type {classes.ButtonWidget|null}
       */
      _autofitButton: null,

      /**
       * The "Fit to table" columns size button
       * @type {classes.ButtonWidget|null}
       */
      _fitToTableButton: null,

      /**
       * DOM: tab bar element (role=tablist)
       * @type {HTMLElement|null}
       */
      _tabsElement: null,

      /**
       * Tab buttons, keyed by tab name
       * @type {?Object<string, HTMLElement>}
       */
      _tabElements: null,

      /**
       * DOM: tab panels, keyed by tab name
       * @type {?Object<string, HTMLElement>}
       */
      _tabPanelElements: null,

      /**
       * True when the VM supports multi-column sort.
       * When false (older VMs), the Sort tab degrades to a single rule, without badge nor Delete.
       * @type {boolean}
       */
      _multiSortSupported: false,

      /**
       * DOM: sort rules list element
       * @type {HTMLElement|null}
       */
      _sortRulesElement: null,

      /**
       * Current sort rules model, in priority order
       * @type {?Array<{colIndex: number, order: string, kind: string}>}
       */
      _sortRules: null,

      /**
       * Pending rule added by "+ Add sort", not applied until a column is chosen
       * @type {?{colIndex: ?number, order: string, kind: string}}
       */
      _pendingSortRule: null,

      /**
       * The "Add sort" button
       * @type {classes.ButtonWidget|null}
       */
      _sortAddButton: null,

      /**
       * The "Delete sort" button
       * @type {classes.ButtonWidget|null}
       */
      _sortDeleteButton: null,

      /**
       * ComboBox widgets of the sort rule rows, recreated on each refresh
       * @type {?Array<classes.ComboBoxWidget>}
       */
      _sortRuleComboWidgets: null,

      /**
       * DOM: sort rule row being dragged (priority reorder)
       * @type {HTMLElement|null}
       */
      _draggedSortRow: null,

      /**
       * DOM: sort rule row hovered during the drag
       * @type {HTMLElement|null}
       */
      _hoveredSortRow: null,

      /**
       * Whether the drop indicator is above (true) or below (false) the hovered row
       * @type {boolean}
       */
      _sortDropAbove: false,

      /**
       * One-shot focus target for the next rules rebuild, when a move changed the focused rule index
       * @type {?{index: number, selector: ?string}}
       */
      _sortRulesFocusOverride: null,

      /**
       * Index of the sort rule row carrying the tab stop — the rules list is a single Tab stop
       * @type {number}
       */
      _sortRulesRovingIndex: 0,

      /**
       * Bound handlers for the sort rules drag & drop
       * @type {Function|null}
       */
      _boundOnSortRulesPointerDown: null,
      _boundOnSortRulesPointerMove: null,
      _boundOnSortRulesPointerUp: null,

      /**
       * DOM: visually hidden aria-live region announcing action results to screen readers
       * @type {HTMLElement|null}
       */
      _liveRegionElement: null,

      /**
       * @inheritDoc
       */
      constructor: function (opts) {
        const currentApp = gbc.SessionService.getCurrent()?.getCurrentApplication();
        const appId = currentApp?.applicationInfo?.appId ?? -1;
        // Older VMs only support a single sort rule (sortColumn/sortType instead of sortSpec).
        // The flag comes from the table widget so rendering and sort applying share one source.
        // Set before the super constructor: _buildContent runs during it.
        this._multiSortSupported = Boolean(opts?.isMultiColumnSortSupported);
        $super.constructor.call(this, {
          ...opts,
          storedSettingsKey: {
            appId: appId,
            formName: "_genero_table_config_",
            windowName: "_tableconfig_"
          }
        });
      },

      /**
       * @inheritDoc
       */
      _initContainerElement: function () {
        $super._initContainerElement.call(this);
        this.setHeader(gbc.I18NService.t("gwc.tableConfig.title"));
        this.setAriaAttribute("label", gbc.I18NService.t("gwc.tableConfig.title"));
        this.setClosable(true, true);

        this._listWidget = new cls.FilterableListWidget(this.getBuildParameters());
        this._listWidget.setParentWidget(this);
        this._listWidget.onVisibilityToggle(this._onVisibilityToggle.bind(this));
        this._listWidget.onReorder(this._onColumnsReordered.bind(this));
        this._listWidget.onShowAll(this._onShowAll.bind(this));
        this._listWidget.setTitleText(gbc.I18NService.t("gwc.tableConfig.columns"));
        this._listWidget.setSearchPlaceholder(gbc.I18NService.t("gwc.tableConfig.searchColumnName"));
        this._listWidget.setGroupSeparatorTitle(gbc.I18NService.t("gwc.tableConfig.frozenColumns"));
        this._listWidget.addMenuAction(
          gbc.I18NService.t("gwc.contextMenu.hideAllColumns"),
          this._onHideAllColumns.bind(this),
          function () {
            return Boolean(this._tableWidget?.isDisplayMode());
          }.bind(this),
          "zmdi-eye-off"
        );
        this._listWidget.addMenuAction(gbc.I18NService.t("gwc.tableConfig.resetView"), this._onResetView.bind(this), null, "zmdi-backup-restore");

        this.setContent(this._buildContent());
      },

      /**
       * Build the panel content: the Sort / Customize tab bar and its two tab panels
       * @returns {HTMLElement}
       * @private
       */
      _buildContent: function () {
        const content = document.createElement("div");
        content.className = "gbc_TableConfigSidePanelWidget__sections";

        content.appendChild(this._buildTabBar());
        this._tabPanelElements = {
          sort: this._buildTabPanel("sort"),
          customize: this._buildTabPanel("customize")
        };
        this._buildSortPanelContent(this._tabPanelElements.sort);
        content.appendChild(this._tabPanelElements.sort);
        content.appendChild(this._tabPanelElements.customize);

        // outside the tab panels: a hidden tab panel would mute its live announcements
        this._liveRegionElement = document.createElement("div");
        this._liveRegionElement.className = "gbc_TableConfigSidePanelWidget__liveRegion";
        this._liveRegionElement.setAttribute("aria-live", "polite");
        content.appendChild(this._liveRegionElement);

        const customizePanel = this._tabPanelElements.customize;
        const sizeTitle = document.createElement("div");
        sizeTitle.className = "gbc_TableConfigSidePanelWidget__sectionTitle";
        sizeTitle.textContent = gbc.I18NService.t("gwc.tableConfig.columnsSize");
        customizePanel.appendChild(sizeTitle);

        const sizeActions = document.createElement("div");
        sizeActions.className = "gbc_TableConfigSidePanelWidget__sizeActions";
        this._autofitButton = this._buildActionButton(
          "zmdi-table-column-width",
          gbc.I18NService.t("gwc.tableConfig.autofit"),
          this._onAutofit.bind(this)
        );
        this._fitToTableButton = this._buildActionButton(
          "zmdi-fit-to-page-outline",
          gbc.I18NService.t("gwc.tableConfig.fitToTable"),
          this._onFitToTable.bind(this)
        );
        sizeActions.appendChild(this._autofitButton.getElement());
        sizeActions.appendChild(this._fitToTableButton.getElement());
        customizePanel.appendChild(sizeActions);

        customizePanel.appendChild(this._listWidget.getElement());

        this._selectTab("customize");
        return content;
      },

      /**
       * Build the Sort tab content: the sort rules list and its footer actions
       * @param {HTMLElement} panelElement - the Sort tab panel
       * @private
       */
      _buildSortPanelContent: function (panelElement) {
        this._sortRulesElement = document.createElement("ul");
        this._sortRulesElement.className = "gbc_TableConfigSidePanelWidget__sortRules";
        // layout grid: one Tab stop for the whole rules list, arrows navigate rows and their controls
        this._sortRulesElement.setAttribute("role", "grid");
        this._sortRulesElement.setAttribute("aria-label", gbc.I18NService.t("gwc.tableConfig.sort"));
        this._boundOnSortRulesPointerDown = this._onSortRulesPointerDown.bind(this);
        this._boundOnSortRulesPointerMove = this._onSortRulesPointerMove.bind(this);
        this._boundOnSortRulesPointerUp = this._onSortRulesPointerUp.bind(this);
        this._sortRulesElement.addEventListener("pointerdown", this._boundOnSortRulesPointerDown);
        panelElement.appendChild(this._sortRulesElement);

        const actions = document.createElement("div");
        actions.className = "gbc_TableConfigSidePanelWidget__sortActions";

        this._sortAddButton = this._buildActionButton("zmdi-plus", gbc.I18NService.t("gwc.tableConfig.addSort"), this._onAddSortRule.bind(this));
        this._sortAddButton.addClasses("gbc_TableConfigSidePanelWidget__addSort");
        actions.appendChild(this._sortAddButton.getElement());

        this._sortDeleteButton = this._buildActionButton(
          "zmdi-delete",
          gbc.I18NService.t("gwc.tableConfig.deleteSort"),
          this._onDeleteAllSortRules.bind(this)
        );
        this._sortDeleteButton.addClasses("gbc_TableConfigSidePanelWidget__deleteSort");
        actions.appendChild(this._sortDeleteButton.getElement());

        panelElement.appendChild(actions);
      },

      /**
       * Build the Sort / Customize tab bar
       * @returns {HTMLElement}
       * @private
       */
      _buildTabBar: function () {
        this._tabsElement = document.createElement("div");
        this._tabsElement.className = "gbc_TableConfigSidePanelWidget__tabs";
        this._tabsElement.setAttribute("role", "tablist");
        this._tabElements = {
          sort: this._buildTab("sort", gbc.I18NService.t("gwc.tableConfig.sort")),
          customize: this._buildTab("customize", gbc.I18NService.t("gwc.tableConfig.customize"))
        };
        return this._tabsElement;
      },

      /**
       * Build one tab button and append it to the tab bar
       * @param {string} key - tab name
       * @param {string} label - displayed text
       * @returns {HTMLElement}
       * @private
       */
      _buildTab: function (key, label) {
        const tab = document.createElement("button");
        tab.type = "button";
        tab.className = "gbc_TableConfigSidePanelWidget__tab";
        tab.id = `${this.getRootClassName()}_tab_${key}`;
        tab.setAttribute("role", "tab");
        tab.setAttribute("aria-selected", "false");
        tab.setAttribute("aria-controls", `${this.getRootClassName()}_panel_${key}`);
        tab.tabIndex = -1;
        tab.dataset.tab = key;
        tab.textContent = label;
        this._tabsElement.appendChild(tab);
        return tab;
      },

      /**
       * Build one tab panel
       * @param {string} key - tab name
       * @returns {HTMLElement}
       * @private
       */
      _buildTabPanel: function (key) {
        const panel = document.createElement("div");
        panel.className = "gbc_TableConfigSidePanelWidget__tabPanel";
        panel.id = `${this.getRootClassName()}_panel_${key}`;
        panel.setAttribute("role", "tabpanel");
        panel.setAttribute("aria-labelledby", `${this.getRootClassName()}_tab_${key}`);
        panel.hidden = true;
        return panel;
      },

      /**
       * Display the given tab: single tab stop on the tab bar, only the matching panel visible
       * @param {string} key - tab name
       * @private
       */
      _selectTab: function (key) {
        if (!this._tabElements) {
          return;
        }
        for (const tabKey of Object.keys(this._tabElements)) {
          const selected = tabKey === key;
          this._tabElements[tabKey].setAttribute("aria-selected", String(selected));
          this._tabElements[tabKey].tabIndex = selected ? 0 : -1;
          this._tabPanelElements[tabKey].hidden = !selected;
        }
      },

      /**
       * @inheritDoc
       */
      manageKeyDown: function (keyString, domKeyEvent, repeat) {
        if (this._manageTabsKeyDown(keyString, domKeyEvent)) {
          return true;
        }
        // covers the rule stops that are not widgets (the rows and the × button): the rule
        // combos reach both handlers through their own patched managePriorityKeyDown
        if (this._manageSortRuleAltArrow(keyString, domKeyEvent)) {
          return true;
        }
        if (this._manageSortRulesNavKey(keyString, domKeyEvent)) {
          return true;
        }
        return $super.manageKeyDown.call(this, keyString, domKeyEvent, repeat);
      },

      /**
       * Keyboard interaction on the tab bar (ARIA tabs pattern, automatic activation):
       * Left/Right cycle through the tabs, Home/End jump to the first/last one.
       * Reached through the system widget key routing, the tabs are plain DOM buttons.
       * @param {string} keyString - normalized key
       * @param {KeyboardEvent} domKeyEvent
       * @returns {boolean} true when the key was handled
       * @private
       */
      _manageTabsKeyDown: function (keyString, domKeyEvent) {
        if (!this._tabElements) {
          return false;
        }
        const tabs = Object.values(this._tabElements);
        const index = tabs.indexOf(domKeyEvent.target);
        if (index === -1) {
          return false;
        }
        let next = null;
        switch (keyString) {
          case "left":
            next = tabs[index - 1] || tabs.at(-1);
            break;
          case "right":
            next = tabs[index + 1] || tabs[0];
            break;
          case "home":
            next = tabs[0];
            break;
          case "end":
            next = tabs.at(-1);
            break;
          default:
            return false;
        }
        this._selectTab(next.dataset.tab);
        next.focus();
        return true;
      },

      /**
       * @inheritDoc
       */
      domFocus: function (preventScroll = false) {
        // dialog entry point: the selected tab — same pattern as the settings modal first field
        const tabs = this._tabElements ? Object.values(this._tabElements) : [];
        const selectedTab = tabs.find((tab) => tab.getAttribute("aria-selected") === "true");
        if (selectedTab) {
          cls.DOMElementHelper.domFocus(selectedTab, preventScroll);
        } else {
          $super.domFocus.call(this, preventScroll);
        }
      },

      /**
       * @inheritDoc
       */
      manageMouseClick: function (domEvent) {
        const tab = domEvent.target.closest(".gbc_TableConfigSidePanelWidget__tab");
        if (tab) {
          this._selectTab(tab.dataset.tab);
        } else if (domEvent.target.closest(".gbc_TableConfigSidePanelWidget__sortRuleRemove")) {
          this._onRemoveSortRule(domEvent);
        }
        return $super.manageMouseClick.call(this, domEvent);
      },

      /**
       * Build one panel action button
       * @param {string} icon - zmdi icon name
       * @param {string} label - button text
       * @param {Function} callback - fired on click
       * @returns {classes.ButtonWidget}
       * @private
       */
      _buildActionButton: function (icon, label, callback) {
        /** @type {classes.ButtonWidget} */
        const button = cls.WidgetFactory.createWidget("Button", this.getBuildParameters());
        button.setParentWidget(this);
        button.setText(label);
        button.setAlignment("verticalCenter", "horizontalCenter");
        button.setImage(icon);
        button.when(context.constants.widgetEvents.click, callback);
        return button;
      },

      /**
       * Announce a message to screen readers through the panel live region
       * @param {string} text
       */
      announce: function (text) {
        if (!this._liveRegionElement) {
          return;
        }
        // cleared first so the same message announced twice in a row is read again
        this._liveRegionElement.textContent = "";
        this.afterDomMutator(
          function () {
            if (!this.isDestroyed()) {
              this._liveRegionElement.textContent = text;
            }
          }.bind(this)
        );
      },

      /**
       * Resize all columns to fit their content
       * @private
       */
      _onAutofit: function () {
        if (this._tableWidget) {
          this._tableWidget.autofitAllColumns();
        }
      },

      /**
       * Resize all columns to fill the table width
       * @private
       */
      _onFitToTable: function () {
        if (this._tableWidget) {
          this._tableWidget.fitToViewAllColumns();
        }
      },

      /**
       * Hide every column but the first visible one, as the context menu entry does
       * @private
       */
      _onHideAllColumns: function () {
        if (!this._tableWidget?.isDisplayMode()) {
          return;
        }
        for (const col of this._tableWidget.getOrderedColumns()) {
          if (col.isVisible()) {
            col.hideOtherColumns();
            this.announce(gbc.I18NService.t("gwc.tableConfig.columnsHiddenAnnounce"));
            return;
          }
        }
      },

      /**
       * Restore the default column settings, as the context menu "Reset settings" entry does
       * @private
       */
      _onResetView: function () {
        if (this._tableWidget) {
          this._tableWidget.emit(context.constants.widgetEvents.tableResetToDefault);
          this.announce(gbc.I18NService.t("gwc.tableConfig.viewResetAnnounce"));
        }
      },

      /**
       * @inheritDoc
       */
      destroy: function () {
        if (this._tableColumnsChangedHandle) {
          this._tableColumnsChangedHandle();
          this._tableColumnsChangedHandle = null;
        }
        if (this._autofitButton) {
          this._autofitButton.destroy();
          this._autofitButton = null;
        }
        if (this._fitToTableButton) {
          this._fitToTableButton.destroy();
          this._fitToTableButton = null;
        }
        if (this._listWidget) {
          this._listWidget.destroy();
          this._listWidget = null;
        }
        if (this._sortAddButton) {
          this._sortAddButton.destroy();
        }
        if (this._sortDeleteButton) {
          this._sortDeleteButton.destroy();
        }
        this._cleanupSortRuleDrag();
        if (this._sortRulesElement && this._boundOnSortRulesPointerDown) {
          this._sortRulesElement.removeEventListener("pointerdown", this._boundOnSortRulesPointerDown);
        }
        this._boundOnSortRulesPointerDown = null;
        this._boundOnSortRulesPointerMove = null;
        this._boundOnSortRulesPointerUp = null;
        this._sortRulesFocusOverride = null;
        this._liveRegionElement = null;
        if (this._sortRuleComboWidgets) {
          for (const combo of this._sortRuleComboWidgets) {
            combo.destroy();
          }
          this._sortRuleComboWidgets = null;
        }
        this._tabsElement = null;
        this._tabElements = null;
        this._tabPanelElements = null;
        this._sortRulesElement = null;
        this._sortRules = null;
        this._pendingSortRule = null;
        this._sortAddButton = null;
        this._sortDeleteButton = null;
        this._columnsById = null;
        this._tableWidget = null;
        $super.destroy.call(this);
      },

      /**
       * Set the table widget whose configuration is edited and populate the list
       * @param {classes.TableWidgetBase} tableWidget
       */
      setTableWidget: function (tableWidget) {
        this._tableWidget = tableWidget;
        this._tableColumnsChangedHandle = tableWidget.when(context.constants.widgetEvents.tableColumnsChanged, this._scheduleRefresh.bind(this));
        this._populateFromTable();
      },

      /**
       * Read the table columns and (re)populate the list
       * @private
       */
      _populateFromTable: function () {
        if (!this._tableWidget || !this._listWidget) {
          return;
        }

        const orderedColumns = this._tableWidget.getOrderedColumns();
        const visibleCount = orderedColumns.filter((col) => !col.isAlwaysHidden() && col.isVisible()).length;
        // frozen zones are reorder boundaries (getDragLimit): mapped to list groups, drawn with a separator
        const leftFrozenCount = this._tableWidget.getLeftFrozenColumns();
        const rightFrozenStart = orderedColumns.length - this._tableWidget.getRightFrozenColumns();

        this._columnsById = new Map();
        const items = [];
        for (let i = 0; i < orderedColumns.length; i++) {
          const col = orderedColumns[i];
          if (col.isAlwaysHidden()) {
            continue;
          }
          const id = col.getRootClassName();
          this._columnsById.set(id, col);
          let group = null;
          if (i < leftFrozenCount) {
            group = "frozenLeft";
          } else if (i >= rightFrozenStart) {
            group = "frozenRight";
          }
          items.push({
            id: id,
            label: col.getText(),
            group: group,
            data: {
              visible: col.isVisible(),
              movable: col.isMovable(),
              // same rule as the context menu checkboxes: the last visible column cannot be hidden
              hidable: col.isCheckable() && !(col.isVisible() && visibleCount === 1),
              stateText: group ? gbc.I18NService.t("gwc.tableConfig.columnFrozen") : ""
            }
          });
        }
        this._listWidget.setItems(items);
        this._populateSortRules();
      },

      /**
       * Sync the local sort rules model from the table sort spec and render it
       * @private
       */
      _populateSortRules: function () {
        if (!this._sortRulesElement || !this._tableWidget) {
          return;
        }
        const columns = this._tableWidget.getColumns();
        // getSortSpec is in priority order; clone the entries as they are mutated on user actions
        this._sortRules = this._tableWidget
          .getSortSpec()
          .filter((rule) => columns[rule.colIndex])
          .map((rule) => ({ colIndex: rule.colIndex, order: rule.order, kind: rule.kind }));
        this._renderSortRules();
      },

      /**
       * Rebuild the sort rule rows, footer and tab badge from the local rules model,
       * so the row indexes always match _sortRules
       * @private
       */
      _renderSortRules: function () {
        if (!this._sortRulesElement || !this._tableWidget) {
          return;
        }
        const columns = this._tableWidget.getColumns();

        // a priority move already computed where the focus must land
        const focusRestore = this._sortRulesFocusOverride || this._captureSortRulesFocus();
        this._sortRulesFocusOverride = null;

        if (this._sortRuleComboWidgets) {
          for (const combo of this._sortRuleComboWidgets) {
            combo.destroy();
          }
        }
        this._sortRuleComboWidgets = [];

        this._sortRulesElement.replaceChildren();
        for (let i = 0; i < this._sortRules.length; i++) {
          this._sortRulesElement.appendChild(this._buildSortRuleRow(this._sortRules[i], i, columns));
        }
        if (this._pendingSortRule) {
          this._sortRulesElement.appendChild(this._buildSortRuleRow(this._pendingSortRule, this._sortRules.length, columns));
        }

        // simple-sort mode (older VMs): a single rule at most
        const canAddRule = this._multiSortSupported || (this._sortRules.length === 0 && !this._pendingSortRule);
        this._sortAddButton.setEnabled(canAddRule && !this._pendingSortRule && this._getAvailableSortColumns().length > 0);
        // no rule at all: only the Add button remains; in simple-sort mode the row × is enough
        const hasAnyRule = this._sortRules.length > 0 || Boolean(this._pendingSortRule);
        this._sortDeleteButton.toggleClass("hidden", !hasAnyRule || !this._multiSortSupported);
        this._sortDeleteButton.setEnabled(Boolean(this._pendingSortRule) || this._sortRules.some((rule) => rule.kind === "U"));
        // the pending rule is not an active sort: it is not counted in the badge (multi-sort only)
        this._updateSortTabBadge(this._multiSortSupported ? this._sortRules.length : 0);

        const rows = this._sortRulesElement.children;
        if (rows.length > 0) {
          this._setSortRulesRoving(rows[Math.min(this._sortRulesRovingIndex, rows.length - 1)]);
        }
        this._restoreSortRulesFocus(focusRestore);
      },

      /**
       * Remember which sort rule control has the focus, before the rows rebuild destroys it
       * @returns {?{index: number, selector: ?string}}
       * @private
       */
      _captureSortRulesFocus: function () {
        const activeElement = document.activeElement;
        if (!this._sortRulesElement.contains(activeElement)) {
          return null;
        }
        const controlClasses = [
          "gbc_TableConfigSidePanelWidget__sortRuleColumn",
          "gbc_TableConfigSidePanelWidget__sortRuleDirection",
          "gbc_TableConfigSidePanelWidget__sortRuleRemove"
        ];
        const activeRow = activeElement.closest(".gbc_TableConfigSidePanelWidget__sortRule");
        const activeControl = activeElement.closest(controlClasses.map((cl) => `.${cl}`).join(", "));
        // the scoping class is not the first one on the combo roots: resolve it from the known list
        return {
          index: activeRow ? Number(activeRow.dataset.ruleIndex) : 0,
          selector: activeControl ? `.${controlClasses.find((cl) => activeControl.classList.contains(cl))}` : null
        };
      },

      /**
       * Put the focus back on the row or control captured before the rows rebuild
       * @param {?{index: number, selector: ?string}} focusRestore
       * @private
       */
      _restoreSortRulesFocus: function (focusRestore) {
        if (!focusRestore) {
          return;
        }
        const rows = this._sortRulesElement.children;
        const row = rows[Math.min(focusRestore.index, rows.length - 1)];
        if (!row) {
          if (this._sortAddButton.isEnabled()) {
            this._sortAddButton.getElement().querySelector("button")?.focus();
          }
          return;
        }
        const target = focusRestore.selector ? row.querySelector(focusRestore.selector) : null;
        // the combo classes sit on the widget root: focus its input
        let focusable = null;
        if (target) {
          focusable = target.matches("input, button") ? target : target.querySelector("input");
        }
        this._focusSortRuleStop(row, focusable && !focusable.disabled ? focusable : row);
      },

      /**
       * Build one sort rule row: drag handle, column dropdown, direction dropdown, remove button
       * @param {{colIndex: number, order: string, kind: string}} rule - sort rule
       * @param {number} index - rule position in the list
       * @param {classes.RTableColumnWidget[]} columns - table columns (model order)
       * @returns {HTMLElement}
       * @private
       */
      _buildSortRuleRow: function (rule, index, columns) {
        const isProgramSort = rule.kind === "P";
        const isPending = rule.colIndex === null;
        const columnText = isPending ? "" : columns[rule.colIndex].getText();

        const row = document.createElement("li");
        row.className = "gbc_TableConfigSidePanelWidget__sortRule";
        row.setAttribute("role", "row");
        row.tabIndex = -1;
        row.dataset.ruleIndex = String(index);
        if (isProgramSort) {
          // program-defined sort (DIALOG.setGroupBy): locked, only its direction can change
          row.classList.add("program-sort");
        }
        if (isPending) {
          row.classList.add("pending-sort");
        }

        const dragHandle = document.createElement("span");
        dragHandle.className = "gbc_TableConfigSidePanelWidget__sortRuleHandle";
        dragHandle.setAttribute("aria-hidden", "true");
        dragHandle.innerHTML = `<i class="zmdi ${isProgramSort ? "zmdi-lock-outline" : "zmdi-drag-vertical"}"></i>`;
        row.appendChild(dragHandle);

        // columns already used by another rule are left out to prevent duplicates
        const columnItems = this._getSortableColumns()
          .filter((col) => !this._sortRules.some((other) => other !== rule && other.colIndex === col.getColumnIndex()))
          .map((col) => ({ text: col.getText(), value: String(col.getColumnIndex()) }));
        const columnCombo = this._buildRuleComboBox(
          "gbc_TableConfigSidePanelWidget__sortRuleColumn",
          "gwc.tableConfig.sortRuleColumn",
          columnItems,
          isPending ? null : String(rule.colIndex)
        );
        if (isPending) {
          columnCombo.setPlaceHolder(gbc.I18NService.t("gwc.tableConfig.chooseColumn"));
        }
        const lockedTextId = `${this.getRootClassName()}_ruleLocked_${index}`;
        if (isProgramSort) {
          columnCombo.setEnabled(false);
          // the lock must not be conveyed by styling alone: describe it on the row controls
          columnCombo.setAriaAttribute("describedby", lockedTextId);
        }
        columnCombo.when(context.constants.widgetEvents.valueChanged, () => {
          this._onSortRuleComboChange(index, true, columnCombo.getValue());
        });
        columnCombo.getElement().setAttribute("role", "gridcell");
        row.appendChild(columnCombo.getElement());

        // wrapper hosting the direction icon displayed inside the closed field
        const directionWrap = document.createElement("span");
        directionWrap.className = "gbc_TableConfigSidePanelWidget__sortRuleDirectionWrap";
        directionWrap.setAttribute("role", "gridcell");
        const ascending = rule.order === "+";
        directionWrap.innerHTML = isProgramSort ? `<i class="zmdi zmdi-circle-small" aria-hidden="true"></i>` : "";
        const directionIcon = document.createElement("i");
        directionIcon.className = `zmdi ${ascending ? "zmdi-arrow-up-thin" : "zmdi-arrow-down-thin"}`;
        directionIcon.setAttribute("aria-hidden", "true");
        directionWrap.appendChild(directionIcon);

        const directionCombo = this._buildRuleComboBox(
          "gbc_TableConfigSidePanelWidget__sortRuleDirection",
          "gwc.tableConfig.sortRuleDirection",
          [
            { text: gbc.I18NService.t("gwc.tableConfig.ascending"), value: "+" },
            { text: gbc.I18NService.t("gwc.tableConfig.descending"), value: "-" }
          ],
          rule.order
        );
        directionCombo.when(context.constants.widgetEvents.valueChanged, () => {
          this._onSortRuleComboChange(index, false, directionCombo.getValue());
        });
        if (isProgramSort) {
          directionCombo.setAriaAttribute("describedby", lockedTextId);
        }
        directionWrap.appendChild(directionCombo.getElement());
        row.appendChild(directionWrap);

        if (isProgramSort) {
          // visually hidden explanation of the lock, read on the row controls through aria-describedby
          const lockedTextElement = document.createElement("span");
          lockedTextElement.className = "gbc_TableConfigSidePanelWidget__sortRuleLockedText";
          lockedTextElement.id = lockedTextId;
          lockedTextElement.textContent = gbc.I18NService.t("gwc.tableConfig.programSortLocked");
          directionWrap.appendChild(lockedTextElement);
        }

        // a program sort rule cannot be removed: its × is disabled and hidden by CSS,
        // keeping the cell footprint so the combos stay aligned across rows
        const removeButton = document.createElement("button");
        removeButton.type = "button";
        removeButton.className = "gbc_TableConfigSidePanelWidget__sortRuleRemove";
        removeButton.tabIndex = -1;
        if (isProgramSort) {
          removeButton.disabled = true;
        } else {
          // I18NService.t() takes no interpolation options — substitute placeholders manually (GBC convention)
          removeButton.setAttribute("aria-label", gbc.I18NService.t("gwc.tableConfig.removeSortRule").replace("{name}", columnText));
        }
        removeButton.innerHTML = `<i class="zmdi zmdi-close" aria-hidden="true"></i>`;
        const removeCell = document.createElement("span");
        removeCell.setAttribute("role", "gridcell");
        removeCell.appendChild(removeButton);
        row.appendChild(removeCell);

        return row;
      },

      /**
       * Returns the columns that can appear in a sort rule, in visual order
       * @returns {classes.RTableColumnWidget[]}
       * @private
       */
      _getSortableColumns: function () {
        return this._tableWidget.getOrderedColumns().filter((col) => !col.isAlwaysHidden() && col.isSortable());
      },

      /**
       * Returns the sortable columns not used by any sort rule yet
       * @returns {classes.RTableColumnWidget[]}
       * @private
       */
      _getAvailableSortColumns: function () {
        const used = new Set(this._sortRules.map((rule) => rule.colIndex));
        return this._getSortableColumns().filter((col) => !used.has(col.getColumnIndex()));
      },

      /**
       * Build one sort rule ComboBox, wired as the standalone settings ones are
       * @param {string} className - scoping class added on the widget root
       * @param {string} ariaLabelKey - locale key of the accessible name
       * @param {Array<{text: string, value: string}>} items - dropdown items
       * @param {?string} value - current value, null to leave it empty
       * @returns {classes.ComboBoxWidget}
       * @private
       */
      _buildRuleComboBox: function (className, ariaLabelKey, items, value) {
        /** @type {classes.ComboBoxWidget} */
        const combo = cls.WidgetFactory.createWidget("ComboBox", this.getBuildParameters());
        combo.setParentWidget(this);
        combo.addClasses(className);
        combo.setNotNull(true);
        combo.setEnabled(true);
        combo.setAriaAttribute("label", gbc.I18NService.t(ariaLabelKey));
        combo.setItems(items);
        if (value !== null) {
          // fromVM=true, as the standalone settings combos do: plain display, no editing state
          combo.setValue(value, true);
        }
        combo.when(context.constants.widgetEvents.openDropDown, () => {
          combo.openDropDown();
        });
        // the rules list is a single Tab stop: its controls are reached with the arrow keys
        const comboInput = combo.getElement().querySelector("input");
        if (comboInput) {
          comboInput.tabIndex = -1;
        }
        // Alt+Up/Down reorders the rule and closed-dropdown arrows navigate the grid:
        // both claimed before the ComboBox arrow behaviors (value change, caret move)
        const superPriorityKeyDown = combo.managePriorityKeyDown.bind(combo);
        combo.managePriorityKeyDown = (keyString, domKeyEvent, repeat) => {
          if (this._manageSortRuleAltArrow(keyString, domKeyEvent)) {
            return true;
          }
          if (!combo.getDropDown().isVisible()) {
            if (keyString === "enter" || keyString === "return") {
              combo.openDropDown();
              return true;
            }
            if (this._manageSortRulesNavKey(keyString, domKeyEvent)) {
              return true;
            }
          }
          return superPriorityKeyDown(keyString, domKeyEvent, repeat);
        };
        this._sortRuleComboWidgets.push(combo);
        return combo;
      },

      /**
       * Handle a column or direction change on a sort rule.
       * On the pending rule, choosing a column turns it into an applied rule.
       * @param {number} index - rule position in the list
       * @param {boolean} isColumnChange - true for the column combo, false for the direction one
       * @param {string} value - the new combo value
       * @private
       */
      _onSortRuleComboChange: function (index, isColumnChange, value) {
        if (!this._sortRules) {
          return;
        }
        const isPendingRow = Boolean(this._pendingSortRule) && index === this._sortRules.length;

        if (isPendingRow) {
          if (isColumnChange) {
            // the pending rule becomes an applied one once its column is chosen
            this._sortRules.push({ colIndex: Number(value), order: this._pendingSortRule.order, kind: "U" });
            this._pendingSortRule = null;
            this._emitSortRules();
          } else {
            this._pendingSortRule.order = value;
            this._scheduleSortRulesRender();
          }
          return;
        }

        const rule = this._sortRules[index];
        if (!rule) {
          return;
        }
        if (isColumnChange) {
          rule.colIndex = Number(value);
        } else {
          rule.order = value;
        }
        this._emitSortRules();
      },

      /**
       * Remove the sort rule of the clicked row
       * @param {MouseEvent} domEvent
       * @private
       */
      _onRemoveSortRule: function (domEvent) {
        const row = domEvent.target.closest(".gbc_TableConfigSidePanelWidget__sortRule");
        if (!row || !this._sortRules) {
          return;
        }
        const index = Number(row.dataset.ruleIndex);
        if (this._pendingSortRule && index === this._sortRules.length) {
          // the pending rule is local only: no VM round-trip needed
          this._pendingSortRule = null;
          this._scheduleSortRulesRender();
          return;
        }
        this._sortRules.splice(index, 1);
        this._emitSortRules();
      },

      /**
       * Add a pending sort rule — applied once the user chooses its column
       * @private
       */
      _onAddSortRule: function () {
        if (this._pendingSortRule || !this._getAvailableSortColumns().length) {
          return;
        }
        this._pendingSortRule = { colIndex: null, order: "-", kind: "U" };
        this._scheduleSortRulesRender(
          function () {
            // let the user pick right away
            this._sortRulesElement.lastElementChild?.querySelector(".gbc_TableConfigSidePanelWidget__sortRuleColumn input")?.focus();
          }.bind(this)
        );
      },

      /**
       * Re-render the sort rules once the current event dispatch is done —
       * rebuilding right away would destroy elements the mouse pipeline still traverses
       * @param {Function} [afterRender] - executed after the rows are rebuilt
       * @private
       */
      _scheduleSortRulesRender: function (afterRender) {
        this.afterDomMutator(
          function () {
            if (this.isDestroyed()) {
              return;
            }
            this._renderSortRules();
            if (afterRender) {
              afterRender();
            }
          }.bind(this)
        );
      },

      /**
       * True when the row is an applied user rule — program and pending rows cannot be reordered
       * @param {?HTMLElement} row
       * @returns {boolean}
       * @private
       */
      _isReorderableSortRow: function (row) {
        if (!row || !this._sortRules) {
          return false;
        }
        const rule = this._sortRules[Number(row.dataset.ruleIndex)];
        return rule?.kind === "U";
      },

      /**
       * Start dragging a sort rule from its handle — priority = list order
       * @param {PointerEvent} evt
       * @private
       */
      _onSortRulesPointerDown: function (evt) {
        if (this._draggedSortRow || !evt.target?.closest(".gbc_TableConfigSidePanelWidget__sortRuleHandle")) {
          return;
        }
        const row = evt.target.closest(".gbc_TableConfigSidePanelWidget__sortRule");
        // a single user rule has nowhere to go
        if (!this._isReorderableSortRow(row) || this._sortRules.filter((rule) => rule.kind === "U").length < 2) {
          return;
        }
        evt.preventDefault();
        this._draggedSortRow = row;
        row.classList.add("dragging");
        document.addEventListener("pointermove", this._boundOnSortRulesPointerMove);
        document.addEventListener("pointerup", this._boundOnSortRulesPointerUp);
      },

      /**
       * Show a drop indicator above or below the hovered rule row
       * @param {PointerEvent} evt
       * @private
       */
      _onSortRulesPointerMove: function (evt) {
        if (!this._draggedSortRow) {
          return;
        }
        evt.preventDefault();
        const elementUnderPointer = document.elementFromPoint(evt.clientX, evt.clientY);
        const row = elementUnderPointer?.closest(".gbc_TableConfigSidePanelWidget__sortRule");
        if (!row || row === this._draggedSortRow || !this._sortRulesElement.contains(row) || !this._isReorderableSortRow(row)) {
          this._setHoveredSortRow(null);
          return;
        }
        const rect = row.getBoundingClientRect();
        this._setHoveredSortRow(row, evt.clientY < rect.top + rect.height / 2);
      },

      /**
       * Drop the dragged rule on the hovered row and clean the drag state up
       * @private
       */
      _onSortRulesPointerUp: function () {
        const dragged = this._draggedSortRow;
        const target = this._hoveredSortRow;
        const dropAbove = this._sortDropAbove;
        this._cleanupSortRuleDrag();
        if (dragged && target && this._sortRulesElement.contains(dragged)) {
          this._reorderSortRule(Number(dragged.dataset.ruleIndex), Number(target.dataset.ruleIndex), dropAbove);
        }
      },

      /**
       * Set the row hovered during a drag and its drop indicator
       * @param {?HTMLElement} row
       * @param {boolean} [isAbove] - true to drop before the row, false to drop after
       * @private
       */
      _setHoveredSortRow: function (row, isAbove) {
        if (this._hoveredSortRow) {
          this._hoveredSortRow.classList.remove("dragover-above", "dragover-below");
        }
        this._hoveredSortRow = row;
        this._sortDropAbove = Boolean(isAbove);
        if (row) {
          row.classList.toggle("dragover-above", this._sortDropAbove);
          row.classList.toggle("dragover-below", !this._sortDropAbove);
        }
      },

      /**
       * Remove the drag state and its document listeners
       * @private
       */
      _cleanupSortRuleDrag: function () {
        if (this._draggedSortRow) {
          this._draggedSortRow.classList.remove("dragging");
        }
        this._setHoveredSortRow(null);
        this._draggedSortRow = null;
        this._sortDropAbove = false;
        document.removeEventListener("pointermove", this._boundOnSortRulesPointerMove);
        document.removeEventListener("pointerup", this._boundOnSortRulesPointerUp);
      },

      /**
       * Alt+Up/Down on a rule row moves it one priority position, as in the columns list
       * @param {string} keyString - normalized key
       * @param {KeyboardEvent} domKeyEvent
       * @returns {boolean} true when the key was handled
       * @private
       */
      _manageSortRuleAltArrow: function (keyString, domKeyEvent) {
        if ((keyString !== "up" && keyString !== "down") || !domKeyEvent.altKey) {
          return false;
        }
        const row = domKeyEvent.target.closest(".gbc_TableConfigSidePanelWidget__sortRule");
        if (!this._isReorderableSortRow(row)) {
          return false;
        }
        this._moveSortRulePriority(Number(row.dataset.ruleIndex), keyString === "up" ? -1 : 1);
        return true;
      },

      /**
       * Grid navigation inside the sort rules (layout grid pattern): Up/Down move between
       * rules, Left/Right between the controls of a rule, Home/End jump to the first/last
       * rule, Escape and Enter step out of/into the row controls.
       * Reached from the panel key routing (rows, × button) and from the combos patch.
       * @param {string} keyString - normalized key
       * @param {KeyboardEvent} domKeyEvent
       * @returns {boolean} true when the key was handled
       * @private
       */
      _manageSortRulesNavKey: function (keyString, domKeyEvent) {
        if (domKeyEvent.altKey || domKeyEvent.ctrlKey || domKeyEvent.metaKey) {
          return false;
        }
        const row = domKeyEvent.target.closest(".gbc_TableConfigSidePanelWidget__sortRule");
        if (!row) {
          return false;
        }
        switch (keyString) {
          case "left":
          case "right":
            this._moveSortRuleCellFocus(row, domKeyEvent.target, keyString === "right" ? 1 : -1);
            return true;
          case "up":
          case "down":
            this._focusSortRuleSibling(row, keyString === "up" ? -1 : 1);
            return true;
          case "home":
            this._focusSortRuleStop(this._sortRulesElement.firstElementChild, this._sortRulesElement.firstElementChild);
            return true;
          case "end":
            this._focusSortRuleStop(this._sortRulesElement.lastElementChild, this._sortRulesElement.lastElementChild);
            return true;
          case "enter":
          case "return":
            // on the row itself: step into its first control — the combos open on their own Enter
            if (domKeyEvent.target === row) {
              const firstControl = row.querySelector("input:not([disabled]), button:not([disabled])");
              if (firstControl) {
                this._focusSortRuleStop(row, firstControl);
              }
              return true;
            }
            return false;
          case "esc":
            // step back to row navigation; a second Escape closes the panel
            if (domKeyEvent.target !== row) {
              this._focusSortRuleStop(row, row);
              return true;
            }
            return false;
          default:
            return false;
        }
      },

      /**
       * Move the focus to the previous/next stop of a rule row: the row itself, then its controls
       * @param {HTMLElement} row
       * @param {EventTarget} currentTarget - the element holding the focus
       * @param {number} delta - 1 for right, -1 for left
       * @private
       */
      _moveSortRuleCellFocus: function (row, currentTarget, delta) {
        const stops = [row, ...row.querySelectorAll("input:not([disabled]), button:not([disabled])")];
        const target = stops[stops.indexOf(currentTarget) + delta];
        if (target) {
          this._focusSortRuleStop(row, target);
        }
      },

      /**
       * Focus the previous/next rule row
       * @param {HTMLElement} row
       * @param {number} delta - -1 for up, 1 for down
       * @private
       */
      _focusSortRuleSibling: function (row, delta) {
        const sibling = delta < 0 ? row.previousElementSibling : row.nextElementSibling;
        if (sibling) {
          this._focusSortRuleStop(sibling, sibling);
        }
      },

      /**
       * Focus a control of a sort rule row and move the rules tab stop onto that row
       * @param {?HTMLElement} row
       * @param {?HTMLElement} stopElement - the row itself or one of its controls
       * @private
       */
      _focusSortRuleStop: function (row, stopElement) {
        if (!row || !stopElement) {
          return;
        }
        this._setSortRulesRoving(row);
        stopElement.focus();
      },

      /**
       * Put the single rules tab stop on the given row
       * @param {HTMLElement} row
       * @private
       */
      _setSortRulesRoving: function (row) {
        const rows = Array.from(this._sortRulesElement.children);
        this._sortRulesRovingIndex = Math.max(rows.indexOf(row), 0);
        for (const other of rows) {
          other.tabIndex = other === row ? 0 : -1;
        }
      },

      /**
       * Move a user rule one priority position up or down, skipping the locked program rules
       * @param {number} index - rule position in the list
       * @param {number} delta - -1 to raise the priority, 1 to lower it
       * @private
       */
      _moveSortRulePriority: function (index, delta) {
        const rule = this._sortRules[index];
        const userRules = this._sortRules.filter((other) => other.kind === "U");
        const target = userRules[userRules.indexOf(rule) + delta];
        if (!target) {
          return;
        }
        const focusRestore = this._captureSortRulesFocus();
        if (this._reorderSortRule(index, this._sortRules.indexOf(target), delta < 0)) {
          // the rebuilt focus must follow the rule to its new position
          this._sortRulesFocusOverride = { index: this._sortRules.indexOf(rule), selector: focusRestore ? focusRestore.selector : null };
        }
      },

      /**
       * Move a user rule before or after another one — priority = list order.
       * Program rules keep their positions: the user rules reorder within the remaining slots.
       * @param {number} fromIndex - dragged rule position
       * @param {number} targetIndex - drop target rule position
       * @param {boolean} insertAbove - true to insert before the target, false to insert after
       * @returns {boolean} true when the order changed and was sent to the VM
       * @private
       */
      _reorderSortRule: function (fromIndex, targetIndex, insertAbove) {
        const moved = this._sortRules[fromIndex];
        const target = this._sortRules[targetIndex];
        if (!moved || !target || moved === target || moved.kind !== "U" || target.kind !== "U") {
          return false;
        }
        const userRules = this._sortRules.filter((rule) => rule.kind === "U");
        userRules.splice(userRules.indexOf(moved), 1);
        userRules.splice(userRules.indexOf(target) + (insertAbove ? 0 : 1), 0, moved);
        let nextUser = 0;
        const newRules = this._sortRules.map((rule) => (rule.kind === "P" ? rule : userRules[nextUser++]));
        if (newRules.every((rule, i) => rule === this._sortRules[i])) {
          return false;
        }
        this._sortRules = newRules;
        this._emitSortRules();
        return true;
      },

      /**
       * Remove every user sort rule — program sorts (setGroupBy) cannot be removed
       * @private
       */
      _onDeleteAllSortRules: function () {
        this._pendingSortRule = null;
        this._sortRules = this._sortRules.filter((rule) => rule.kind === "P");
        this._emitSortRules();
      },

      /**
       * Send the current sort rules to the VM — the acknowledgement refreshes the list
       * @private
       */
      _emitSortRules: function () {
        if (this._tableWidget) {
          this._tableWidget.emit(
            context.constants.widgetEvents.tableSetSortSpec,
            this._sortRules.map((rule) => ({ colIndex: rule.colIndex, order: rule.order, kind: rule.kind }))
          );
          this._announceSortState();
          // re-render right away so the row indexes match the mutated rules — a second
          // action must not target rows still indexed on the pre-mutation model
          this._scheduleSortRulesRender();
        }
      },

      /**
       * Announce the full sort state — assistive tech support of per-header attributes is uneven,
       * a single live message guarantees every screen reader user gets the resulting state
       * @private
       */
      _announceSortState: function () {
        if (this._sortRules.length === 0) {
          this.announce(gbc.I18NService.t("gwc.tableConfig.sortRemovedAnnounce"));
          return;
        }
        const columns = this._tableWidget.getColumns();
        const rules = this._sortRules
          .map((rule, index) =>
            gbc.I18NService.t("gwc.tableConfig.sortRuleAnnounce")
              .replace("{name}", columns[rule.colIndex].getText())
              .replace("{direction}", gbc.I18NService.t(rule.order === "+" ? "gwc.tableConfig.ascending" : "gwc.tableConfig.descending"))
              .replace("{position}", String(index + 1))
          )
          .join("; ");
        this.announce(gbc.I18NService.t("gwc.tableConfig.sortAnnounce").replace("{rules}", rules));
      },

      /**
       * Update the sort count badge on the Sort tab.
       * The visible number is aria-hidden: the count goes in the tab accessible name.
       * @param {number} count - number of active sort rules
       * @private
       */
      _updateSortTabBadge: function (count) {
        const tab = this._tabElements?.sort;
        if (!tab) {
          return;
        }
        let badge = tab.querySelector(".gbc_TableConfigSidePanelWidget__tabBadge");
        if (!badge) {
          badge = document.createElement("span");
          badge.className = "gbc_TableConfigSidePanelWidget__tabBadge";
          badge.setAttribute("aria-hidden", "true");
          tab.appendChild(badge);
        }
        badge.textContent = String(count);
        badge.classList.toggle("hidden", count === 0);
        if (count > 0) {
          // I18NService.t() takes no interpolation options — substitute placeholders manually (GBC convention)
          tab.setAttribute("aria-label", gbc.I18NService.t("gwc.tableConfig.sortTabLabel").replace("{count}", String(count)));
        } else {
          tab.removeAttribute("aria-label");
        }
      },

      /**
       * Repopulate once per update batch (setOrder/setHidden fire once per column)
       * @private
       */
      _scheduleRefresh: function () {
        if (this._refreshQueued) {
          return;
        }
        this._refreshQueued = true;
        this.afterDomMutator(
          function () {
            this._refreshQueued = false;
            if (!this.isDestroyed()) {
              this._populateFromTable();
            }
          }.bind(this)
        );
      },

      /**
       * Toggle the column bound to the list item — the VM acknowledgement refreshes the list
       * @param {Event} event
       * @param {classes.FilterableListWidget} src
       * @param {{id: string}} data
       * @private
       */
      _onVisibilityToggle: function (event, src, data) {
        if (!this._columnsById) {
          return;
        }
        const col = this._columnsById.get(data.id);
        if (col) {
          col.emit(context.constants.widgetEvents.tableShowHideCol, "toggle");
        }
      },

      /**
       * Show every user-hidable column, as the context menu "Show all columns" entry does
       * @private
       */
      _onShowAll: function () {
        if (!this._tableWidget) {
          return;
        }
        for (const col of this._tableWidget.getColumns()) {
          if (!col.isAlwaysHidden() && col.isHidable() && !col.isVisible()) {
            col.emit(context.constants.widgetEvents.tableShowHideCol, "show");
          }
        }
        this.announce(gbc.I18NService.t("gwc.tableConfig.allColumnsVisibleAnnounce"));
      },

      /**
       * Apply the new list order to the table, mirroring the header drag & drop reorder
       * @param {Event} event
       * @param {classes.FilterableListWidget} src
       * @param {{orderedIds: Array<string>}} data
       * @private
       */
      _onColumnsReordered: function (event, src, data) {
        if (!this._tableWidget || !this._columnsById) {
          return;
        }

        const currentColumns = this._tableWidget.getOrderedColumns();
        const listedColumns = data.orderedIds.map((id) => this._columnsById.get(id));

        // The list is stale (columns changed during the drag) — snap it back instead of applying
        const listableCount = currentColumns.filter((col) => !col.isAlwaysHidden()).length;
        if (listedColumns.includes(undefined) || listedColumns.length !== listableCount) {
          this._scheduleRefresh();
          return;
        }

        // Rebuild the full order: always-hidden columns keep their index, the others follow the list order
        const reorderedColumns = new Array(currentColumns.length);
        for (let i = 0; i < currentColumns.length; i++) {
          if (currentColumns[i].isAlwaysHidden()) {
            reorderedColumns[i] = currentColumns[i];
          }
        }
        let listIndex = 0;
        for (let i = 0; i < reorderedColumns.length; i++) {
          if (!reorderedColumns[i]) {
            reorderedColumns[i] = listedColumns[listIndex];
            listIndex++;
          }
        }

        // Reject moves outside a column drag limit (frozen zones) — the refresh snaps the list back
        for (let i = 0; i < reorderedColumns.length; i++) {
          const col = reorderedColumns[i];
          if (col.getOrderedColumnIndex() !== i) {
            const dragLimit = col.getDragLimit();
            if (i !== cls.MathHelper.clamp(i, dragLimit.min, dragLimit.max)) {
              this._scheduleRefresh();
              return;
            }
          }
        }

        for (let i = 0; i < reorderedColumns.length; i++) {
          reorderedColumns[i].setOrder(i, true);
        }
        this._tableWidget.update(true, false, true);
        // And only after emit signal to send tabIndex to VM
        for (let i = 0; i < reorderedColumns.length; i++) {
          reorderedColumns[i].emit(context.constants.widgetEvents.tableOrderColumn, i);
        }
      }
    };
  });

  cls.WidgetFactory.registerBuilder("TableConfigSidePanel", cls.TableConfigSidePanelWidget);
});
